import BreadCrumb from "Components/Common/BreadCrumb";
import React, { useState, useMemo, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, Badge, Button as RButton, Button, Nav, NavItem, NavLink, Input, Spinner } from "reactstrap";
import Heading from "Components/Common/Heading";
import Filter from "Components/Common/Filter";
import CreatePollModal from "./CreatePollModal";
import EditPollModal from "./EditPollModal";
import PollTimer from "./PollTimer";
import PollDetailsModal from "./PollDetailsModal";
import classnames from "classnames";
import { getAllAssemblies, getVoteResults, closeAgendaItem } from "endpoints/voting/index.action";
import { getAllCondos } from "endpoints/condo/index.action";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "slices/store";
import { toast } from "react-toastify";
import "./Polls.scss";

interface Poll {
  id: string;
  shortCode?: string; // User-friendly poll ID (e.g., "POL-A1B2C3D4")
  title: string;
  description: string;
  condo: string;
  condoId?: string;
  status: "active" | "closed";
  closingDate: string;
  closingTime: string;
  startDate?: string; // Start date formatted
  startTime?: string; // Start time formatted
  creationDate?: string; // Creation date formatted
  creationTime?: string; // Creation time formatted
  votingStart?: string; // ISO 8601 date string for timer
  votingEnd?: string; // ISO 8601 date string for timer
  options?: { text: string; votes?: number }[];
  mostVoted?: string;
  createdBy?: string;
  createdAt?: string;
  assemblyId?: string;
}

const Polls = () => {
  const { t } = useTranslation();
  document.title = t("polls.title");

  // Get current logged-in admin user
  const { admin } = useSelector((state: RootState) => state.Admin);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"all" | "active" | "closed">("all");
  const [selectedCondo, setSelectedCondo] = useState<string>("all");
  const [polls, setPolls] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [condos, setCondos] = useState<Array<{ id: string; name: string; condoNumber?: string }>>([]);
  const [selectedPoll, setSelectedPoll] = useState<Poll | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedPollForEdit, setSelectedPollForEdit] = useState<Poll | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [closingPollId, setClosingPollId] = useState<string | null>(null);

  // Fetch assemblies and condos
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch assemblies and condos in parallel
      const [assembliesResponse, condosResponse] = await Promise.all([
        getAllAssemblies({ limit: 100, page: 1 }),
        getAllCondos({ limit: 100, page: 1 }),
      ]);

      // Extract condos data
      if (condosResponse?.data) {
        const condosList = Array.isArray(condosResponse.data)
          ? condosResponse.data
          : condosResponse.data.condos || [];
        setCondos(condosList);
      }

      // Extract assemblies data
      const assembliesData = assembliesResponse?.data?.assemblies || assembliesResponse?.data || [];

      // Filter assemblies to only show those created by the current logged-in admin
      const currentAdminId = admin?.id;
      const filteredAssemblies = currentAdminId
        ? assembliesData.filter((assembly: any) => {
            // Check if assembly was created by current admin
            const createdById = assembly.createdBy?.id || assembly.createdById;
            return createdById === currentAdminId;
          })
        : [];

      // Map assemblies and agenda items to polls
      const pollsList: Poll[] = [];

      for (const assembly of filteredAssemblies) {
        const agendaItems = assembly.agendaItems || [];

        for (const agendaItem of agendaItems) {
          const now = new Date();
          const votingStart = new Date(agendaItem.votingStart);
          const votingEnd = new Date(agendaItem.votingEnd);
          // Determine status: closed if ended, active if started and not ended, upcoming if not started
          const status = now > votingEnd ? "closed" : (now >= votingStart ? "active" : "active"); // Keep as "active" for timer display

          // Format closing dates
          const closingDate = new Date(agendaItem.votingEnd);
          const formattedClosingDate = closingDate.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          });
          const formattedClosingTime = closingDate.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          });

          // Format start dates
          const startDate = new Date(agendaItem.votingStart);
          const formattedStartDate = startDate.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          });
          const formattedStartTime = startDate.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          });

          // Format creation dates
          const creationDate = new Date(agendaItem.createdAt || assembly.createdAt);
          const formattedCreationDate = creationDate.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          });
          const formattedCreationTime = creationDate.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          });

          // Get vote results if closed
          let voteResults = null;
          if (status === "closed") {
            try {
              const resultsResponse = await getVoteResults(agendaItem.id);
              voteResults = resultsResponse?.data;
            } catch (err) {
              console.error("Error fetching vote results:", err);
            }
          }

          // Use custom options from agenda item, or fallback to yes/no/abstain
          const pollOptions = agendaItem.options && agendaItem.options.length > 0
            ? agendaItem.options
            : ['Yes', 'No', 'Abstain'];

          // Map options with vote counts
          const options = pollOptions.map((optionText: string) => {
            if (status === "closed" && voteResults?.statistics?.optionVotes) {
              // Use dynamic option votes if available
              const voteCount = voteResults.statistics.optionVotes[optionText] || 0;
              return { text: optionText, votes: voteCount };
            } else if (status === "closed" && voteResults?.statistics) {
              // Fallback to yes/no/abstain counts for backward compatibility
              const voteCount =
                optionText === 'Yes' ? (voteResults.statistics.yesVotes || 0) :
                  optionText === 'No' ? (voteResults.statistics.noVotes || 0) :
                    optionText === 'Abstain' ? (voteResults.statistics.abstainVotes || 0) : 0;
              return { text: optionText, votes: voteCount };
            } else {
              return { text: optionText };
            }
          });

          // Determine most voted option
          let mostVoted: string | undefined;
          if (voteResults?.statistics) {
            if (voteResults.statistics.mostVotedOption) {
              mostVoted = voteResults.statistics.mostVotedOption;
            } else {
              // Fallback for backward compatibility
              const { yesVotes, noVotes, abstainVotes } = voteResults.statistics;
              if (yesVotes > noVotes && yesVotes > abstainVotes) {
                mostVoted = "Yes";
              } else if (noVotes > yesVotes && noVotes > abstainVotes) {
                mostVoted = "No";
              } else if (abstainVotes > yesVotes && abstainVotes > noVotes) {
                mostVoted = "Abstain";
              }
            }
          }

          const condoName = assembly.condo?.name || assembly.condo?.condoNumber || "Unknown";

          pollsList.push({
            id: agendaItem.id,
            shortCode: agendaItem.shortCode || `POL-${agendaItem.id.substring(0, 8).toUpperCase()}`,
            title: agendaItem.title,
            description: agendaItem.description || "",
            condo: condoName,
            condoId: assembly.condoId,
            status,
            closingDate: formattedClosingDate,
            closingTime: formattedClosingTime,
            startDate: formattedStartDate,
            startTime: formattedStartTime,
            creationDate: formattedCreationDate,
            creationTime: formattedCreationTime,
            votingStart: agendaItem.votingStart, // ISO string for timer
            votingEnd: agendaItem.votingEnd, // ISO string for timer
            options: status === "closed" ? options : options.map((opt: { text: string }) => ({ text: opt.text })),
            mostVoted,
            createdBy: assembly.createdBy
              ? `${assembly.createdBy.firstName || ""} ${assembly.createdBy.lastName || ""}`.trim() || assembly.createdBy.email
              : "Admin",
            createdAt: formattedCreationDate,
            assemblyId: assembly.id,
          });
        }
      }

      setPolls(pollsList);
    } catch (err: any) {
      console.error("Error fetching polls:", err);
      setError(err?.message || t("polls.failedToLoadCondominiums"));
    } finally {
      setLoading(false);
    }
  };

  // Fetch assemblies and condos on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Listen for timer expiration events to refresh data
  useEffect(() => {
    const handleTimerExpired = () => {
      // Refresh poll data when a timer expires
      fetchData();
    };

    window.addEventListener('pollTimerExpired', handleTimerExpired);
    return () => {
      window.removeEventListener('pollTimerExpired', handleTimerExpired);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh polls when a new one is created
  const handlePollCreated = () => {
    fetchData();
  };

  // Get unique condos from fetched condos or polls
  const availableCondos = useMemo(() => {
    if (condos.length > 0) {
      return condos.map(c => c.condoNumber || c.name).filter(Boolean).sort();
    }
    const condosFromPolls = Array.from(new Set(polls.map((poll) => poll.condo))).sort();
    return condosFromPolls;
  }, [polls, condos]);

  // Filter polls based on tab, search, and condo
  const filteredPolls = useMemo(() => {
    let filtered = polls;

    // Filter by tab (status)
    if (activeTab === "active") {
      filtered = filtered.filter((poll) => poll.status === "active");
    } else if (activeTab === "closed") {
      filtered = filtered.filter((poll) => poll.status === "closed");
    }

    // Filter by condo
    if (selectedCondo !== "all") {
      filtered = filtered.filter((poll) => poll.condo === selectedCondo);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (poll) =>
          poll.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          poll.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          poll.condo.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [polls, activeTab, selectedCondo, searchQuery]);

  const toggleCreateModal = () => {
    setIsCreateModalOpen(!isCreateModalOpen);
  };

  const handleClosePoll = async (pollId: string) => {
    if (closingPollId) return; // Prevent multiple simultaneous closes
    
    try {
      setClosingPollId(pollId);
      
      // Use dedicated close endpoint that allows closing at any time
      const response = await closeAgendaItem(pollId);

      if (response?.status === 'success') {
        toast.success(t("polls.pollClosedSuccessfully") || "Poll closed successfully");
        // Refresh the polls list
        await fetchData();
      } else {
        console.error("Failed to close poll:", response?.message || response?.response);
        toast.error(response?.message || response?.response || t("polls.failedToClosePoll") || "Failed to close poll. Please try again.");
      }
    } catch (error: any) {
      console.error("Error closing poll:", error);
      const errorMessage = error?.message || error?.response || t("polls.errorClosingPoll") || "An error occurred while closing the poll. Please try again.";
      toast.error(errorMessage);
    } finally {
      setClosingPollId(null);
    }
  };

  const handleTabChange = (tab: "all" | "active" | "closed") => {
    setActiveTab(tab);
  };

  const handleShowMore = (poll: Poll) => {
    setSelectedPoll(poll);
    setIsDetailsModalOpen(true);
  };

  const toggleDetailsModal = () => {
    setIsDetailsModalOpen(!isDetailsModalOpen);
    if (isDetailsModalOpen) {
      setSelectedPoll(null);
    }
  };

  const handleEditPoll = (poll: Poll) => {
    // Edge case: Check if poll can be edited (not started and not closed)
    const now = new Date();
    const votingStart = poll.votingStart ? new Date(poll.votingStart) : null;

    if (!votingStart) {
      console.error("Cannot edit poll: Invalid voting start time");
      return;
    }

    if (now >= votingStart) {
      console.warn("Cannot edit poll: Voting has already started");
      return;
    }

    setSelectedPollForEdit(poll);
    setIsEditModalOpen(true);
  };

  const toggleEditModal = () => {
    setIsEditModalOpen(!isEditModalOpen);
    if (isEditModalOpen) {
      setSelectedPollForEdit(null);
    }
  };

  const handlePollUpdated = () => {
    fetchData();
    toggleEditModal();
  };

  // Check if description needs truncation (more than 3 lines)
  const needsTruncation = (text: string): boolean => {
    // Approximate check: if text is longer than ~150 characters, it likely needs truncation
    // This is a simple heuristic - CSS will handle the actual line clamping
    return text.length > 150;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("polls.title")}
            pageTitles={[
              { label: t("condominiums.home"), path: "/dashboard" },
              { label: t("polls.title") },
            ]}
          />

          <Row className="align-items-center justify-content-between mb-4">
            <Col xs={12} lg={8} className="mb-3 mb-lg-0">
              <Heading
                title={t("polls.manageCommunityPolls")}
                description={t("polls.createAndManageDescription")}
                button={false}
              />
            </Col>
            <Col xs={12} lg={4}>
              <div className="d-flex flex-column flex-sm-row gap-3 align-items-stretch align-items-sm-center">
                <div className="flex-grow-1">
                  <Filter
                    placeholder={t("polls.searchPolls")}
                    value={searchQuery}
                    onSearch={setSearchQuery}
                    onChange={setSearchQuery}
                  />
                </div>
                <RButton
                  color="primary"
                  onClick={toggleCreateModal}
                  className="create-poll-btn"
                >
                  <i className="ri-add-circle-line me-2"></i>
                  {t("polls.createNewPoll")}
                </RButton>
              </div>
            </Col>
          </Row>

          {/* Tabs and Condo Filter */}
          <Row className="mb-4">
            <Col xs={12} md={8}>
              <Nav
                className="polls-tabs"
                style={{
                  borderRadius: "8px",
                  display: "inline-flex",
                  padding: "3px",
                  alignItems: "center",
                  backgroundColor: "#F3F4F6",
                }}
              >
                {[
                  { key: "all", label: t("polls.all") },
                  { key: "active", label: t("polls.active") },
                  { key: "closed", label: t("polls.closed") },
                ].map((tab, idx) => {
                  const isActive = activeTab === tab.key;
                  const baseStyle: React.CSSProperties = {
                    cursor: "pointer",
                    padding: "8px 18px",
                    borderRadius: 8,
                    marginRight: idx < 2 ? 8 : 0,
                    transition: "all 120ms ease",
                    fontWeight: 500,
                    fontSize: 14,
                  };
                  const activeStyle: React.CSSProperties = {
                    background: "#ffffff",
                    color: "#000",
                    boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
                    border: "1px solid rgba(16,24,40,0.06)",
                  };
                  const inactiveStyle: React.CSSProperties = {
                    background: "transparent",
                    color: "#6b7280",
                    border: "none",
                  };

                  return (
                    <NavItem key={tab.key}>
                      <NavLink
                        className={classnames({ active: isActive })}
                        onClick={() => handleTabChange(tab.key as "all" | "active" | "closed")}
                        style={{
                          ...baseStyle,
                          ...(isActive ? activeStyle : inactiveStyle),
                          ...(idx > 0
                            ? {
                              borderLeft: "1px solid rgba(0,0,0,0.06)",
                              marginLeft: 6,
                              paddingLeft: 14,
                            }
                            : {}),
                        }}
                      >
                        {tab.label}
                      </NavLink>
                    </NavItem>
                  );
                })}
              </Nav>
            </Col>
            <Col xs={12} md={4} className="d-flex justify-content-end align-items-center">
              <div className="condo-filter-wrapper">
                <label htmlFor="condo-filter" className="me-2 mb-0" style={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: 500 }}>
                  {t("polls.selectCondo")}:
                </label>
                <Input
                  type="select"
                  id="condo-filter"
                  value={selectedCondo}
                  onChange={(e) => setSelectedCondo(e.target.value)}
                  className="condo-filter-select"
                  style={{
                    minWidth: "150px",
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                    padding: "0.5rem 0.75rem",
                    fontSize: "0.875rem",
                  }}
                >
                  <option value="all">{t("polls.allCondos")}</option>
                  {availableCondos.map((condo) => (
                    <option key={condo} value={condo}>
                      {condo}
                    </option>
                  ))}
                </Input>
              </div>
            </Col>
          </Row>

          {/* Polls Grid */}
          <Row>
            {loading ? (
              <Col xs={12} className="text-center p-5">
                <Spinner color="primary" />
                <p className="text-muted mt-3">{t("common.loading")}</p>
              </Col>
            ) : error ? (
              <Col xs={12}>
                <Card className="text-center p-5">
                  <CardBody>
                    <i className="ri-error-warning-line text-danger" style={{ fontSize: "3rem" }}></i>
                    <p className="text-danger mt-3 mb-0">{error}</p>
                    <RButton color="primary" className="mt-3" onClick={() => window.location.reload()}>
                      {t("condominiums.retry")}
                    </RButton>
                  </CardBody>
                </Card>
              </Col>
            ) : filteredPolls.length === 0 ? (
              <Col xs={12}>
                <Card className="text-center p-5">
                  <CardBody>
                    <i className="ri-file-list-3-line text-muted" style={{ fontSize: "3rem" }}></i>
                    <p className="text-muted mt-3 mb-0">No polls found</p>
                  </CardBody>
                </Card>
              </Col>
            ) : (
              filteredPolls.map((poll) => (
                <Col key={poll.id} xs={12} md={6} lg={4} className="mb-4">
                  <Card className="poll-card">
                    <CardBody className="poll-card-body">
                      {/* Fixed Header */}
                      <div className="poll-header poll-header-fixed">
                        <div className="poll-id-section">
                          <h5 className="poll-id">{poll.shortCode || `POL-${poll.id.substring(0, 8).toUpperCase()}`}</h5>
                          <p className="poll-condo">Condo: {poll.condo}</p>
                        </div>
                        <div className="poll-header-actions">
                          {/* Edit button - only show if poll hasn't started */}
                          {poll.votingStart && (() => {
                            const now = new Date();
                            const votingStart = new Date(poll.votingStart);
                            const canEdit = now < votingStart && poll.status !== "closed";
                            return canEdit ? (
                              <Button
                                color="link"
                                size="sm"
                                className="poll-edit-btn"
                                onClick={() => handleEditPoll(poll)}
                                title="Edit poll"
                              >
                                <i className="ri-edit-line"></i>
                              </Button>
                            ) : null;
                          })()}
                          <Badge
                            color={poll.status === "active" ? "success" : "danger"}
                            className="poll-status-badge"
                          >
                            {poll.status === "active" ? "Active" : "Closed"}
                          </Badge>
                        </div>
                      </div>

                      {/* Scrollable Content Area */}
                      <div className="poll-content-scrollable">
                        <h6 className="poll-title">{poll.title}</h6>
                        <div className="poll-description-wrapper">
                          <p className="poll-description">{poll.description}</p>
                          {needsTruncation(poll.description) && (
                            <Button
                              color="link"
                              className="poll-show-more-btn"
                              onClick={() => handleShowMore(poll)}
                            >
                              Show more
                              <i className="ri-arrow-right-s-line ms-1"></i>
                            </Button>
                          )}
                        </div>

                        {/* Poll Timer - Shows countdown to start or end */}
                        {poll.votingStart && poll.votingEnd && poll.status !== "closed" && (
                          <PollTimer
                            votingStart={poll.votingStart}
                            votingEnd={poll.votingEnd}
                            status={poll.status}
                          />
                        )}

                        {/* Poll Metadata Section */}
                        <div className="poll-metadata-section">
                          <div className="poll-metadata-item">
                            <i className="ri-calendar-check-line me-2"></i>
                            <div className="poll-metadata-content">
                              <span className="poll-metadata-label">Created</span>
                              <span className="poll-metadata-value">{poll.creationDate} at {poll.creationTime}</span>
                            </div>
                          </div>
                          <div className="poll-metadata-item">
                            <i className="ri-play-circle-line me-2"></i>
                            <div className="poll-metadata-content">
                              <span className="poll-metadata-label">Starts</span>
                              <span className="poll-metadata-value">{poll.startDate} at {poll.startTime}</span>
                            </div>
                          </div>
                        </div>

                        {poll.status === "active" ? (
                          <>
                            <div className="poll-closing-info">
                              <i className="ri-time-line me-2"></i>
                              <span>Closing on {poll.closingDate} at {poll.closingTime}</span>
                            </div>
                            <div className="poll-options-section">
                              <p className="poll-options-label">Options:</p>
                              {poll.options?.map((option, idx) => (
                                <div key={idx} className="poll-option-item">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name={`poll-${poll.id}`}
                                    id={`option-${poll.id}-${idx}`}
                                    disabled
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`option-${poll.id}-${idx}`}
                                  >
                                    {option.text}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="poll-closing-info">
                              <i className="ri-time-line me-2"></i>
                              <span>Closed on {poll.closingDate} at {poll.closingTime}</span>
                            </div>
                            <div className="poll-results-section">
                              <p className="poll-results-label">Results:</p>
                              {poll.options?.map((option, idx) => (
                                <div
                                  key={idx}
                                  className={`poll-result-item ${option.text === poll.mostVoted ? "most-voted" : ""
                                    }`}
                                >
                                  <span className="poll-result-option">{option.text}</span>
                                  <Badge color="primary" className="poll-vote-badge">
                                    {option.votes} votes!
                                  </Badge>
                                </div>
                              ))}
                            </div>
                            {poll.mostVoted && (
                              <div className="poll-most-voted">
                                <strong>Most Voted!</strong> {poll.mostVoted}
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* Fixed Footer */}
                      <div className="poll-footer-fixed">
                        {poll.status === "active" ? (
                          <RButton
                            color="primary"
                            className="poll-action-btn"
                            onClick={() => handleClosePoll(poll.id)}
                            disabled={closingPollId === poll.id}
                          >
                            {closingPollId === poll.id ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                Closing...
                              </>
                            ) : (
                              <>
                                Close Voting
                                <i className="ri-arrow-right-line ms-2"></i>
                              </>
                            )}
                          </RButton>
                        ) : null}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))
            )}
          </Row>
        </Container>
      </div>

      <CreatePollModal
        isOpen={isCreateModalOpen}
        toggle={toggleCreateModal}
        onPollCreated={handlePollCreated}
      />

      <PollDetailsModal
        isOpen={isDetailsModalOpen}
        toggle={toggleDetailsModal}
        poll={selectedPoll}
      />

      <EditPollModal
        isOpen={isEditModalOpen}
        toggle={toggleEditModal}
        poll={selectedPollForEdit}
        onPollUpdated={handlePollUpdated}
      />
    </React.Fragment>
  );
};

export default Polls;

